<template>
  <InputText v-if="field.type === CustomFieldType.Text" :id="inputId" v-model="textValue" :invalid="invalid"
    :maxlength="maxLength ?? 255" class="w-full" :data-testid="`custom-field-input-${field.key}`" />
  <Textarea v-else-if="field.type === CustomFieldType.Textarea" :id="inputId" v-model="textValue" :invalid="invalid"
    rows="3" :maxlength="maxLength ?? 5000" autoResize class="w-full" :data-testid="`custom-field-input-${field.key}`" />
  <InputNumber v-else-if="field.type === CustomFieldType.Number" :inputId="inputId" v-model="numberValue"
    :invalid="invalid" :useGrouping="false" :maxFractionDigits="4" class="w-full"
    :data-testid="`custom-field-input-${field.key}`" />
  <DatePicker v-else-if="field.type === CustomFieldType.Date" :inputId="inputId" v-model="dateValue" :invalid="invalid"
    dateFormat="yy-mm-dd" showIcon showButtonBar class="w-full" :data-testid="`custom-field-input-${field.key}`" />
  <Select v-else-if="field.type === CustomFieldType.Select && sourced" :inputId="inputId" v-model="textValue"
    :options="listOptions" optionLabel="label" optionValue="value" :invalid="invalid" showClear filter
    :disabled="waitingForParent" :loading="listLoading" :placeholder="waitingForParent ? waitingText : 'Select an option'"
    class="w-full" :data-testid="`custom-field-input-${field.key}`" />
  <Select v-else-if="field.type === CustomFieldType.Select" :inputId="inputId" v-model="textValue"
    :options="field.options" optionLabel="label" optionValue="value" :invalid="invalid" showClear
    placeholder="Select an option" class="w-full" :data-testid="`custom-field-input-${field.key}`" />
  <div v-else-if="field.type === CustomFieldType.Checkbox" class="flex items-center gap-2">
    <Checkbox :inputId="inputId" v-model="boolValue" binary :invalid="invalid" :aria-label="label || undefined"
      :data-testid="`custom-field-input-${field.key}`" />
    <label :for="inputId" class="text-sm">Yes</label>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CustomFieldType } from '@/types/composition-functions';
import { useNigerianStates } from '@/composables/queries/useNigerianStates';
import {
  awaitingParentChoice, choicesWithCurrent, sourcedChoices,
  type CustomFieldDefinition, type CustomFieldInputValue,
} from '@/utils/customFields';

/**
 * One input for a school-defined custom field, chosen by the field's type (#574).
 * A dropdown may take its choices from a built-in list (Nigerian states or
 * LGAs); an LGA dropdown that follows a state field offers that state's LGAs.
 */
const props = defineProps<{
  field: Pick<CustomFieldDefinition, 'key' | 'type' | 'options' | 'options_source' | 'depends_on'>;
  inputId: string;
  invalid?: boolean;
  /** Field label, read out for the yes/no box whose visible text is just "Yes". */
  label?: string;
  /** Current value of the field this one follows (`field.depends_on`). */
  dependsOnValue?: CustomFieldInputValue;
  /** Label of the followed field, for the "select … first" placeholder. */
  dependsOnLabel?: string;
  /** Longest text accepted, when it is not the custom field default (255, or 5000 for a long answer). */
  maxLength?: number;
}>();

const model = defineModel<CustomFieldInputValue>();

const sourced = computed(() => props.field.type === CustomFieldType.Select && !!props.field.options_source);
const { states, loading: listLoading } = useNigerianStates({ enabled: sourced });

const waitingForParent = computed(() => awaitingParentChoice(props.field, props.dependsOnValue));
const waitingText = computed(() => `Select ${props.dependsOnLabel || 'a state'} first`);
const listOptions = computed(() => choicesWithCurrent(
  sourcedChoices(props.field, states.value, props.dependsOnValue),
  model.value,
));

const textValue = computed({
  get: () => (typeof model.value === 'string' ? model.value : null),
  set: (v: string | null | undefined) => { model.value = v ?? null; },
});
const numberValue = computed({
  get: () => (typeof model.value === 'number' ? model.value : null),
  set: (v: number | null | undefined) => { model.value = v ?? null; },
});
const dateValue = computed({
  get: () => (model.value instanceof Date ? model.value : null),
  set: (v: Date | Date[] | (Date | null)[] | null | undefined) => { model.value = v instanceof Date ? v : null; },
});
const boolValue = computed({
  get: () => model.value === true,
  set: (v: boolean) => { model.value = v; },
});

</script>
